import { useEffect, useRef, useState } from 'react';
import { addDays, formatDate, monthDays, moveMonth, selectRange } from '../pages/bookingDates';

export default function DateRangeCalendar({ today, range, onChange, error }) {
  const currentMonth = today.slice(0, 7);
  const [month, setMonth] = useState(range.start ? range.start.slice(0, 7) : currentMonth);
  const [hovered, setHovered] = useState('');
  const [focused, setFocused] = useState(range.start || today);
  const pendingFocus = useRef(false);
  const calendar = useRef(null);
  const cells = monthDays(month);
  const active = focused.slice(0, 7) === month && focused >= today ? focused : month === currentMonth ? today : `${month}-01`;

  useEffect(() => {
    if (pendingFocus.current) {
      calendar.current.querySelector(`[data-date="${focused}"]`)?.focus();
      pendingFocus.current = false;
    }
  }, [focused, month]);

  function navigate(amount) {
    setMonth(previous => {
      const next = moveMonth(previous, amount);
      return next < currentMonth ? currentMonth : next;
    });
    setHovered('');
  }

  function keyboard(event, day) {
    const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    let next;
    if (event.key in offsets) next = addDays(day, offsets[event.key]);
    else if (event.key === 'Home' || event.key === 'End') {
      const index = cells.indexOf(day) % 7;
      next = addDays(day, event.key === 'Home' ? -index : 6 - index);
    } else if (event.key === 'PageUp' || event.key === 'PageDown') {
      const nextMonth = moveMonth(month, event.key === 'PageUp' ? -1 : 1);
      const lastDay = monthDays(nextMonth).filter(Boolean).length;
      next = `${nextMonth}-${String(Math.min(Number(day.slice(-2)), lastDay)).padStart(2, '0')}`;
    } else return;
    event.preventDefault();
    if (next < today) next = today;
    pendingFocus.current = true;
    setFocused(next);
    setMonth(next.slice(0, 7));
  }

  return (
    <div className="escape-booking__calendar" ref={calendar}>
      <div className="escape-booking__month-nav">
        <button type="button" aria-label="Previous month" disabled={month <= currentMonth} onClick={() => navigate(-1)}>‹</button>
        <h3 aria-live="polite">{formatDate(`${month}-01`, { month: 'long', year: 'numeric' })}</h3>
        <button type="button" aria-label="Next month" onClick={() => navigate(1)}>›</button>
      </div>
      <p id="calendar-instructions" className="escape-booking__calendar-help">Choose check-in, then check-out. Use arrow keys to move between dates.</p>
      <div className="escape-booking__weekdays" aria-hidden="true">{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <span key={day}>{day}</span>)}</div>
      <div className="escape-booking__days" role="group" aria-label="Choose travel dates" aria-describedby={`calendar-instructions${error ? ' booking-dates-error' : ''}`} onMouseLeave={() => setHovered('')}>
        {cells.map((day, index) => {
          if (!day) return <span key={`empty-${index}`} aria-hidden="true" />;
          const endpoint = day === range.start || day === range.end;
          const between = range.start && range.end && day > range.start && day < range.end;
          const preview = range.start && !range.end && hovered > range.start && day > range.start && day <= hovered;
          return (
            <div key={day} className={`escape-booking__day-cell${between ? ' is-between' : ''}${preview ? ' is-preview' : ''}`}>
              <button type="button" data-date={day} disabled={day < today} tabIndex={day === active ? 0 : -1}
                className={endpoint ? 'is-selected' : ''} aria-pressed={endpoint} aria-current={day === today ? 'date' : undefined}
                aria-label={`${formatDate(day, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}${day === range.start ? ', check-in' : day === range.end ? ', check-out' : ''}`}
                onFocus={() => setFocused(day)} onKeyDown={event => keyboard(event, day)} onMouseEnter={() => setHovered(day)}
                onClick={() => { setHovered(''); onChange(selectRange(range, day)); }}>
                {Number(day.slice(-2))}
              </button>
            </div>
          );
        })}
      </div>
      <div className="escape-booking__calendar-bottom">
        <span aria-live="polite">{range.end ? 'Your dates are selected.' : range.start ? 'Now choose your check-out date.' : 'Select your check-in date.'}</span>
        <button type="button" disabled={!range.start} onClick={() => { onChange({ start: '', end: '' }); setHovered(''); }}>Clear dates</button>
      </div>
    </div>
  );
}
